import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import AsyncSelect from "react-select/async";
import {
    Alert,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    FormGroup,
    Input,
    Label,
    Row,
    Spinner,
    Table,
} from "reactstrap";
import * as dynamicConfigService from "../../services/api/dynamic-config-template-service";
import projectService from "../../services/api/projects-service";
import UserContext from "../../UserContext";

const loadProjectOptions = async (inputValue) => {
    const filters = { projectname: inputValue || "", status: 1 };
    const projectsData = await projectService.getAllProjects(filters, 1, 30);
    if (!projectsData.status || !Array.isArray(projectsData.data)) return [];
    return projectsData.data.map((p) => ({
        value: p.id || p._id,
        label: `${p.name || "(no name)"} — ${p.slug || ""}`,
    }));
};

const DynamicProjectConfigAssignments = () => {
    const history = useHistory();
    const context = useContext(UserContext);
    const [selectedProject, setSelectedProject] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState("");
    const [baseVersion, setBaseVersion] = useState("1");
    const [instanceSlug, setInstanceSlug] = useState("");
    const [initPreviewLink, setInitPreviewLink] = useState("");
    const [instances, setInstances] = useState([]);
    const [loadingInstances, setLoadingInstances] = useState(false);
    const [initLoading, setInitLoading] = useState(false);
    const [alert, setAlert] = useState(null);

    useEffect(() => {
        (async () => {
            const res = await dynamicConfigService.listDynamicConfigTemplates();
            if (res.status && res.data) setTemplates(res.data);
        })();
    }, []);

    const refreshInstances = useCallback(async (projectId) => {
        if (!projectId) {
            setInstances([]);
            return;
        }
        setLoadingInstances(true);
        setAlert(null);
        const res = await dynamicConfigService.listProjectInstances(String(projectId));
        setLoadingInstances(false);
        if (res.status && res.data) setInstances(res.data);
        else if (!res.status && res.error?.errorCode === 403) context.setIsAuthorized(context);
        else setAlert({ type: "danger", msg: res.error?.message || "Failed to load instances" });
    }, [context]);

    useEffect(() => {
        if (selectedProject?.value) refreshInstances(selectedProject.value);
        else setInstances([]);
    }, [selectedProject, refreshInstances]);

    const initInstance = async (e) => {
        e.preventDefault();
        if (!selectedProject?.value || !templateId || !baseVersion || !instanceSlug.trim()) return;
        setInitLoading(true);
        setAlert(null);
        const res = await dynamicConfigService.initProjectInstance({
            projectId: String(selectedProject.value),
            templateId,
            baseVersion: Number(baseVersion),
            instanceSlug: instanceSlug.trim(),
            previewLink: initPreviewLink.trim() || undefined,
        });
        setInitLoading(false);
        if (res.status && res.data?.instance?._id) {
            setAlert({ type: "success", msg: "Config instance created." });
            setInstanceSlug("");
            await refreshInstances(selectedProject.value);
            history.push(`/template/dynamic-config-templates/project/${res.data.instance._id}`);
        } else {
            setAlert({ type: "danger", msg: res.error?.message || "Init failed (slug taken or need published base version?)" });
        }
    };

    return (
        <Container fluid className="mt-4">
            <Row>
                <Col md="12">
                    <h3>Project dynamic config assignments</h3>
                    <p className="text-muted">
                        Search and select a project, then create multiple config instances (template + published base
                        version + unique slug per project).
                    </p>
                </Col>
            </Row>
            {alert && (
                <Alert color={alert.type} toggle={() => setAlert(null)}>
                    {alert.msg}
                </Alert>
            )}
            <Row>
                <Col md="12" className="mb-3">
                    <Card>
                        <CardHeader>Project</CardHeader>
                        <CardBody>
                            <FormGroup>
                                <Label>Search project</Label>
                                <AsyncSelect
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={loadProjectOptions}
                                    value={selectedProject}
                                    onChange={setSelectedProject}
                                    placeholder="Type name to search…"
                                />
                            </FormGroup>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col md="6">
                    <Card>
                        <CardHeader>Create config instance</CardHeader>
                        <CardBody>
                            <form onSubmit={initInstance}>
                                <FormGroup>
                                    <Label>Base template</Label>
                                    <Input
                                        type="select"
                                        value={templateId}
                                        onChange={(e) => setTemplateId(e.target.value)}
                                        required
                                        disabled={!selectedProject}
                                    >
                                        <option value="">Select…</option>
                                        {templates.map((t) => (
                                            <option key={t._id} value={t._id}>
                                                {t.name} ({t.templateKey}) — published v{t.currentVersion}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                                <FormGroup>
                                    <Label>Published base version to fork</Label>
                                    <Input
                                        value={baseVersion}
                                        onChange={(e) => setBaseVersion(e.target.value)}
                                        type="number"
                                        min={1}
                                        disabled={!selectedProject}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>Instance slug</Label>
                                    <Input
                                        value={instanceSlug}
                                        onChange={(e) => setInstanceSlug(e.target.value)}
                                        placeholder="e.g. homepage-sections"
                                        disabled={!selectedProject}
                                        required
                                    />
                                    <small className="text-muted">Unique per project; lowercase words separated by hyphens.</small>
                                </FormGroup>
                                <FormGroup>
                                    <Label>Preview URL <span className="text-muted font-weight-normal">(optional)</span></Label>
                                    <Input
                                        type="url"
                                        value={initPreviewLink}
                                        onChange={(e) => setInitPreviewLink(e.target.value)}
                                        placeholder="https://… (shown as QR in pub console)"
                                        disabled={!selectedProject}
                                        autoComplete="off"
                                    />
                                </FormGroup>
                                <Button color="primary" disabled={initLoading || !selectedProject}>
                                    {initLoading ? "…" : "Create instance"}
                                </Button>
                            </form>
                        </CardBody>
                    </Card>
                </Col>
                <Col md="6">
                    <Card>
                        <CardHeader>Instances for selected project</CardHeader>
                        <CardBody>
                            {!selectedProject && (
                                <p className="text-muted small">Select a project to list instances.</p>
                            )}
                            {selectedProject && loadingInstances && <Spinner size="sm" />}
                            {selectedProject && !loadingInstances && instances.length > 0 && (
                                <Table responsive size="sm">
                                    <thead>
                                        <tr>
                                            <th>Slug</th>
                                            <th>Base template</th>
                                            <th>Forked from v</th>
                                            <th>Project pv</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {instances.map((i) => (
                                            <tr key={i._id}>
                                                <td>{i.instanceSlug}</td>
                                                <td>{i.baseTemplateName || i.baseTemplateId}</td>
                                                <td>{i.createdFromBaseVersion}</td>
                                                <td>{i.currentProjectVersion}</td>
                                                <td>
                                                    <Button
                                                        size="sm"
                                                        color="info"
                                                        onClick={() =>
                                                            history.push(
                                                                `/template/dynamic-config-templates/project/${i._id}`
                                                            )
                                                        }
                                                    >
                                                        Edit schema
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                            {selectedProject && !loadingInstances && instances.length === 0 && (
                                <p className="text-muted small">No instances yet for this project.</p>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default DynamicProjectConfigAssignments;
